import type { LocationQuery, LocationQueryValue, RouteLocationNormalizedLoaded } from 'vue-router';

export interface CrossDirectoryLocationRoute {
  params: Record<string, string>;
  query: LocationQuery;
}

export const AGENCY_DIRECTORY_STASH_QUERY_KEYS = [
  'listingType',
  'sortBy',
  'includeChildren',
] as const;

export type AgencyDirectoryStashQueryKey = (typeof AGENCY_DIRECTORY_STASH_QUERY_KEYS)[number];

export type AgencyDirectoryStashQuery = Partial<
  Record<AgencyDirectoryStashQueryKey, LocationQueryValue>
>;

export const AGENT_DIRECTORY_STASH_QUERY_KEYS = ['services', 'specializations'] as const;

export type AgentDirectoryStashQueryKey = (typeof AGENT_DIRECTORY_STASH_QUERY_KEYS)[number];

export type AgentDirectoryStashQuery = Partial<
  Record<AgentDirectoryStashQueryKey, LocationQueryValue>
>;

/** Picks whitelisted query keys for directory tab stash (never restores page). */
export const pickDirectoryStashQueryFromRoute = <K extends string>(
  query: LocationQuery,
  keys: readonly K[],
): Partial<Record<K, LocationQueryValue>> => {
  const stash: Partial<Record<K, LocationQueryValue>> = {};

  for (const key of keys) {
    const value = query[key];

    if (value) {
      stash[key] = value as LocationQueryValue;
    }
  }

  return stash;
};

/** Merges stashed directory filters into a cross-directory query (never restores page). */
export const mergeDirectoryStashQuery = (
  baseQuery: LocationQuery,
  stash: LocationQuery,
): LocationQuery => ({
  ...baseQuery,
  ...stash,
});

/** Merges stashed agency filters into a cross-directory query (never restores page). */
export const mergeAgencyDirectoryStashQuery = (
  baseQuery: LocationQuery,
  stash: AgencyDirectoryStashQuery,
): LocationQuery => mergeDirectoryStashQuery(baseQuery, stash);

/** Merges stashed agent facet filters into a cross-directory query (never restores page). */
export const mergeAgentDirectoryStashQuery = (
  baseQuery: LocationQuery,
  stash: AgentDirectoryStashQuery,
): LocationQuery => mergeDirectoryStashQuery(baseQuery, stash);

/** Location params and query shared when switching between agency and agent directory. */
export const buildCrossDirectoryLocationRoute = (
  route: Pick<RouteLocationNormalizedLoaded, 'params' | 'query'>,
): CrossDirectoryLocationRoute => ({
  params: route.params.location ? { location: String(route.params.location) } : {},
  query: {
    ...(route.query.loc ? { loc: route.query.loc } : {}),
  },
});
